/**
 * BoardChatPanelV2 — V2 floating chat card for the board canvas.
 *
 * Matches the Figma board chat panel (node 12130:124874):
 *  - Rounded-3xl white card with shadow
 *  - Custom header: "Untitled Chat ▾"  |  MessageCirclePlus (new chat) · UnfoldVertical (fill height) · Minus
 *  - Original ChatInterface <header> hidden via CSS
 *  - Vertical footer card: textarea top, agent selector + send btn bottom
 *  - Teal send button (#0e7490), 36×36, rounded-md
 */

import { useRef, useEffect, useCallback, useState } from 'react'
import { ChevronDown, MessageCirclePlus, UnfoldVertical, Maximize2, Minimize2, Minus, MessageSquare, Pencil, Share2 } from 'lucide-react'
import ChatInterface from './index'
import { CatalogScopeToggle } from './CatalogScopeToggle'
import InlineRenameInput from '@/components/InlineRenameInput'
import { useTranslation } from '@/i18n'
import { useAppStore } from '@/store'
import { normalizeAgentName, getDisplayName, AgentId, BACKEND_TO_STORE_AGENT_ID, AGENT_STORE_BG_COLORS, DEFAULT_AGENT_BG_COLOR, type AgentIdType } from '@/constants/agentNames'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { IntuiTooltip } from '@/components/intueri/IntuiTooltip'
import ShareModal from '@/components/ShareModal'
import { isFeatureAllowedInEmbed } from '@/lib/embedFeatures'
import { useParams } from 'react-router-dom'

interface BoardChatPanelV2Props {
  onClose?: () => void
  autoSendMessage?: boolean
  navigationKey?: string
  initialMessage?: string
  isMaximized?: boolean
  isDockedRight?: boolean
  onToggleMaximize?: () => void
  width?: number
  onWidthChange?: (width: number) => void
  height?: number
  onHeightChange?: (height: number) => void
}

function formatRelativeTime(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

const AGENT_AVATARS: Record<string, { image: string; bgColor: string }> = {
  [AgentId.PLANT_SUPPORT]: { image: '/images/plant-support-agent.png', bgColor: '#FEF08A' },
  [AgentId.CHEMICAL]: { image: '/images/chemical-agent.png', bgColor: '#BFDBFE' },
  [AgentId.MECHANICAL]: { image: '/images/mechanical-agent.png', bgColor: '#BBF7D0' },
  [AgentId.ELECTRICAL]: { image: '/images/elec-agent.png', bgColor: '#E9D5FF' },
}
const DEFAULT_AVATAR = AGENT_AVATARS[AgentId.PLANT_SUPPORT]

// Default docked-chat height (px) — mirrors BoardCanvas's initial
// chatPanelHeight. Used to restore the panel when toggling "fill height" off
// if we have no remembered pre-fill height.
const DEFAULT_CHAT_HEIGHT_PX = 560

export default function BoardChatPanelV2({
  onClose,
  autoSendMessage,
  navigationKey,
  initialMessage,
  isMaximized,
  isDockedRight,
  onToggleMaximize,
  onWidthChange,
  height,
  onHeightChange,
}: BoardChatPanelV2Props) {
  const { t } = useTranslation()
  const activeAgent = useAppStore((s) => s.activeAgent)
  const setActiveAgent = useAppStore((s) => s.setActiveAgent)
  const entireDataCatalog = useAppStore((s) => s.entireDataCatalog)
  const setEntireDataCatalog = useAppStore((s) => s.setEntireDataCatalog)
  const storeAgents = useAppStore((s) => s.agents)
  const normalized = normalizeAgentName(activeAgent)
  // FS-574: agents without an alias entry (e.g. maint-agent, epc-agent)
  // used to fall back to a prettified internal id ("Maint-Agent") and the
  // default avatar. Prefer the store persona's proper name and image —
  // it's the same source the agent grid renders from.
  const activePersona = storeAgents.find(
    (a) => a.id === activeAgent || a.id === BACKEND_TO_STORE_AGENT_ID[normalized as AgentIdType],
  )
  const avatar =
    AGENT_AVATARS[normalized] ||
    (activePersona
      ? {
          image: activePersona.image,
          // Same per-agent color the grid uses, so the footer avatar matches.
          bgColor: AGENT_STORE_BG_COLORS[activePersona.id] || DEFAULT_AGENT_BG_COLOR,
        }
      : DEFAULT_AVATAR)
  const agentDisplayName = activePersona?.name || getDisplayName(normalized)

  // Only show agents the workspace has enabled. storeAgents uses store-side
  // IDs (e.g. "chemical-agent") while AGENT_AVATARS is keyed by canonical
  // backend IDs ("chemical_engineer"), so map via BACKEND_TO_STORE_AGENT_ID.
  // When storeAgents is empty (still loading), fall back to all avatars.
  const visibleAgentEntries = (() => {
    if (!storeAgents || storeAgents.length === 0) return Object.entries(AGENT_AVATARS)
    // FS-574: the store lists unavailable agents too — the dropdown only
    // offers available ones.
    const enabledStoreIds = new Set(storeAgents.filter((a) => a.isAvailable).map((a) => a.id))
    return Object.entries(AGENT_AVATARS).filter(([canonical]) => {
      const storeId = BACKEND_TO_STORE_AGENT_ID[canonical as AgentIdType]
      return storeId ? enabledStoreIds.has(storeId) : false
    })
  })()

  // The reset that used to live here now lives on the BOARD route (BoardCanvas),
  // because this panel is no longer the only thing that toggles the flag. The
  // empty-board entry card sets it BEFORE this panel exists, and an unmount
  // cleanup here fires during React's StrictMode double-invoke at MOUNT — so a
  // scope ticked on the entry card was cleared by the panel opening to answer
  // it, in dev only, which is where it would be tested.

  // Agent selector dropdown
  const [agentOpen, setAgentOpen] = useState(false)

  // Chat history dropdown
  const [historyOpen, setHistoryOpen] = useState(false)
  // FS-663: the board hosting this chat — the conversation link is
  // /chat/<boardId>?c=<conversationId>, so a share needs both ids.
  const { board_id: boardId } = useParams()
  const [showShareModal, setShowShareModal] = useState(false)
  const chatConversations = useAppStore((s) => s.chatConversations)
  const currentConversationId = useAppStore((s) => s.currentConversationId)
  const keepBoard = useAppStore((s) => s.keepBoard)

  // UI-84: "running a query" is one of the actions Hasma names as making a
  // board worth keeping, and it is the one the board ROW never sees — a chat
  // writes no operations, so updateBoard never fires for it.
  //
  // The signal is a conversation APPEARING while this board is open, not one
  // merely being set. currentConversationId is global store state that nothing
  // clears on a board change, so a chat opened on a previous board is still
  // there when a new one is created — and keeping on presence alone would keep
  // every new board opened after any chat, which is most of them (review).
  //
  // Declared before the effect below so it re-baselines first on a board
  // change; the comparison then sees "same conversation, different board" and
  // stays quiet.
  const conversationAtOpenRef = useRef<string | null>(null)
  useEffect(() => {
    conversationAtOpenRef.current = currentConversationId ?? null
    // Only `boardId`: re-baselining whenever the conversation changed would
    // erase the very difference this exists to notice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId])

  useEffect(() => {
    if (!boardId || !currentConversationId) return
    if (currentConversationId === conversationAtOpenRef.current) return
    keepBoard(boardId)
  }, [currentConversationId, boardId, keepBoard])
  const selectConversation = useAppStore((s) => s.selectConversation)
  const setCurrentConversationId = useAppStore((s) => s.setCurrentConversationId)
  const setChatMessages = useAppStore((s) => s.setChatMessages)
  const renameConversation = useAppStore((s) => s.renameConversation)

  // FS-539: inline rename state for a conversation row in the history dropdown.
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')

  const untitledLabel = t('chat:interface.untitled_chat', { defaultValue: 'Untitled Chat' })
  // Display title prefers the user-set title, then the first message, then the
  // "Untitled Chat" placeholder (FS-539). Plain fn — only called during render,
  // never passed as a prop, so useCallback would add no value.
  const displayTitle = (conv: { title?: string | null; first_message?: string }) =>
    conv.title?.trim() || conv.first_message?.trim() || untitledLabel

  const startRename = useCallback((id: string, current: string) => {
    setRenamingId(id)
    setRenameDraft(current)
  }, [])

  const commitRename = useCallback(() => {
    if (renamingId) {
      const next = renameDraft.trim()
      if (next) void renameConversation(renamingId, next)
    }
    setRenamingId(null)
    setRenameDraft('')
  }, [renamingId, renameDraft, renameConversation])

  const handleNewChat = useCallback(() => {
    setCurrentConversationId(null)
    setChatMessages([])
    setHistoryOpen(false)
  }, [setCurrentConversationId, setChatMessages])

  // Fill-height toggle. First click expands the panel to (almost) full viewport
  // height and remembers the prior height; clicking again restores that height.
  // `isDockedRight` (height ≥ viewport − 100, computed in BoardCanvas) reflects
  // the filled state and also drives the 90°-clockwise-rotated header icon.
  const prevHeightRef = useRef<number | null>(null)
  const handleToggleFillHeight = useCallback(() => {
    if (!onHeightChange) return
    if (isDockedRight) {
      onHeightChange(prevHeightRef.current ?? DEFAULT_CHAT_HEIGHT_PX)
    } else {
      prevHeightRef.current = height ?? DEFAULT_CHAT_HEIGHT_PX
      onHeightChange(window.innerHeight - 24)
    }
  }, [isDockedRight, height, onHeightChange])

  const handleSelectConversation = useCallback((conversationId: string) => {
    selectConversation(conversationId)
    setHistoryOpen(false)
  }, [selectConversation])

  // Sort conversations by most recent first
  const sortedConversations = [...chatConversations].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  )

  // Determine header label: show first message of current conversation, else
  // "Untitled Chat" (FS-539 — "New Chat" was confusing as a title since it
  // collides with the New Chat starter action).
  const currentConversation = chatConversations.find((c) => c.id === currentConversationId)
  const headerTitle = currentConversation ? displayTitle(currentConversation) : untitledLabel
  const headerLabel = headerTitle.length > 30 ? headerTitle.slice(0, 30) + '...' : headerTitle

  // Resize handles: left edge (width), top edge (height), top-left corner (both)
  const resizeAxis = useRef<'x' | 'y' | 'xy' | null>(null)

  const startResize = useCallback((axis: 'x' | 'y' | 'xy') => (e: React.MouseEvent) => {
    e.preventDefault()
    resizeAxis.current = axis
    const cursors = { x: 'col-resize', y: 'row-resize', xy: 'nwse-resize' }
    document.body.style.cursor = cursors[axis]
    document.body.style.userSelect = 'none'
  }, [])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const axis = resizeAxis.current
      if (!axis) return
      if ((axis === 'x' || axis === 'xy') && onWidthChange) {
        const newWidth = window.innerWidth - e.clientX - 12
        onWidthChange(Math.max(380, Math.min(newWidth, window.innerWidth - 80)))
      }
      if ((axis === 'y' || axis === 'xy') && onHeightChange) {
        const newHeight = window.innerHeight - e.clientY - 12
        onHeightChange(Math.max(300, Math.min(newHeight, window.innerHeight - 80)))
      }
    }
    const handleMouseUp = () => {
      if (!resizeAxis.current) return
      resizeAxis.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [onWidthChange, onHeightChange])

  return (
    <div className={`board-chat-v2 w-full h-full flex flex-col bg-white dark:bg-dark-card ${isMaximized ? 'rounded-none' : isDockedRight ? 'rounded-none border-l border-[#e8eaee] dark:border-dark-border' : 'rounded-3xl shadow-[0_4px_24px_rgba(15,23,42,0.10)] border border-[#e8eaee] dark:border-dark-border'}`}>
      {/* Resize handles (hidden when maximized) */}
      {!isMaximized && (onWidthChange || onHeightChange) && (
        <>
          {/* Left edge → width */}
          {onWidthChange && (
            <div
              onMouseDown={startResize('x')}
              className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-30"
            />
          )}
          {/* Top edge → height */}
          {onHeightChange && (
            <div
              onMouseDown={startResize('y')}
              className="absolute left-0 right-0 top-0 h-1.5 cursor-row-resize z-30"
            />
          )}
          {/* Top-left corner → both */}
          {onWidthChange && onHeightChange && (
            <div
              onMouseDown={startResize('xy')}
              className="absolute left-0 top-0 w-3 h-3 cursor-nwse-resize z-40"
            />
          )}
        </>
      )}

      {/* ── V2 Header ─────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0">
        <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors max-w-[200px]"
            >
              <span className="truncate">{headerLabel}</span>
              <ChevronDown className="w-4 h-4 flex-shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={8}
            className="w-72 p-0 max-h-[400px] flex flex-col"
          >
            {/* FS-539: the in-dropdown "New Chat" button was redundant with the
                MessageCirclePlus icon in the header, so it was removed. */}

            {/* Conversation list */}
            <div className="overflow-y-auto flex-1">
              {sortedConversations.length === 0 ? (
                <div className="py-8 text-center">
                  <MessageSquare className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No conversations yet</p>
                </div>
              ) : (
                sortedConversations.map((conv) => {
                  const isActive = conv.id === currentConversationId
                  const label = displayTitle(conv)
                  const isRenaming = renamingId === conv.id
                  return (
                    <div
                      key={conv.id}
                      className={`group w-full flex items-center gap-2 px-3 py-2.5 transition-colors ${
                        isActive
                          ? 'bg-accent text-foreground'
                          : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                      }`}
                    >
                      <MessageSquare className="w-4 h-4 flex-shrink-0" />
                      {isRenaming ? (
                        // Same two-line structure as the display button, so the
                        // row keeps its exact footprint while renaming.
                        <div className="flex-1 min-w-0 text-left">
                          <InlineRenameInput
                            autoFocus
                            maxLength={255}
                            value={renameDraft}
                            onChange={(e) => setRenameDraft(e.target.value)}
                            onBlur={commitRename}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); commitRename() }
                              else if (e.key === 'Escape') { e.preventDefault(); setRenamingId(null); setRenameDraft('') }
                            }}
                            aria-label={t('chat:interface.rename_chat', { defaultValue: 'Rename chat' })}
                            containerClassName="grid"
                            className="text-sm text-foreground truncate"
                          />
                          <p className="text-xs text-muted-foreground">{formatRelativeTime(conv.updated_at)}</p>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => handleSelectConversation(conv.id)}
                            className="flex-1 min-w-0 text-left"
                          >
                            <p className="text-sm truncate">{label}</p>
                            <p className="text-xs text-muted-foreground">{formatRelativeTime(conv.updated_at)}</p>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); startRename(conv.id, conv.title || conv.first_message || '') }}
                            aria-label={t('chat:interface.rename_chat', { defaultValue: 'Rename chat' })}
                            className="flex-shrink-0 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-accent text-muted-foreground hover:text-foreground transition-opacity"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </PopoverContent>
        </Popover>
        <div className="flex items-center gap-2">
          {/* FS-663. Needs a saved conversation AND a board — an unsent chat
              has no id to grant access to. */}
          {isFeatureAllowedInEmbed('chatShare') && currentConversationId && boardId && (
            <IntuiTooltip content={t('chat:interface.share_chat', { defaultValue: 'Share chat' })}>
              <button
                type="button"
                onClick={() => setShowShareModal(true)}
                aria-label={t('chat:interface.share_chat', { defaultValue: 'Share chat' })}
                className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
              >
                <Share2 className="w-[18px] h-[18px]" />
              </button>
            </IntuiTooltip>
          )}
          <IntuiTooltip content="New Chat">
            <button type="button" onClick={handleNewChat} className="text-muted-foreground hover:text-foreground transition-colors p-0.5">
              <MessageCirclePlus className="w-[18px] h-[18px]" />
            </button>
          </IntuiTooltip>
          <IntuiTooltip content={isDockedRight ? 'Restore height' : 'Fill height'}>
            <button
              type="button"
              onClick={handleToggleFillHeight}
              disabled={!onHeightChange}
              className="text-muted-foreground hover:text-foreground transition-colors p-0.5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <UnfoldVertical className="w-[18px] h-[18px]" />
            </button>
          </IntuiTooltip>
          {onToggleMaximize && (
            <IntuiTooltip content={isMaximized ? 'Exit fullscreen' : 'Fullscreen'}>
              <button type="button" onClick={onToggleMaximize} className="text-muted-foreground hover:text-foreground transition-colors p-0.5">
                {isMaximized ? <Minimize2 className="w-[18px] h-[18px]" /> : <Maximize2 className="w-[18px] h-[18px]" />}
              </button>
            </IntuiTooltip>
          )}
          <IntuiTooltip content="Minimize">
            <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-0.5">
              <Minus className="w-[18px] h-[18px]" />
            </button>
          </IntuiTooltip>
        </div>
      </div>

      {isFeatureAllowedInEmbed('chatShare') && currentConversationId && boardId && (
        <ShareModal
          isOpen={showShareModal}
          setShow={setShowShareModal}
          target={{
            type: 'conversation',
            id: currentConversationId,
            boardId,
            name: currentConversation?.title || 'Chat',
          }}
        />
      )}

      {/* ── ChatInterface (header hidden, footer restyled via CSS) ── */}
      <div className="flex-1 min-h-0 relative">
        <ChatInterface
          context_type="workspace"
          onClose={onClose}
          chatMode="canvas"
          autoSendMessage={autoSendMessage}
          navigationKey={navigationKey}
          initialMessage={initialMessage}
        />

        {/* ── Agent selector — overlaid at bottom-left of the footer input card ──
             `right-` as well as `left-`, so the trigger's width is whatever the
             composer actually leaves rather than a fixed cap (greptile P1 on
             #30952). An unconditional max-width cannot know the panel width: at
             the supported 380px minimum, a 260px label pushed the trigger to
             ~340px while the send cluster starts around 312px, so it could cover
             the send button. The 96px reserve is the button bar — 16px card
             padding plus the attach/send controls, which the scoped CSS pins
             bottom-right. */}
        <div className="absolute bottom-[22px] left-[32px] right-[96px] z-10">
          <Popover open={agentOpen} onOpenChange={setAgentOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                // inline-flex, not flex: a block-level flex box would stretch to
                // the reserved width above and make the empty strip beside the
                // name open the picker. max-w-full is what actually clamps it.
                className="inline-flex max-w-full items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <div
                  className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0"
                  style={{ backgroundColor: avatar.bgColor }}
                >
                  <img src={avatar.image} alt={agentDisplayName} className="w-5 h-5 object-cover" draggable={false} />
                </div>
                {/* UI-49: this was capped at max-w-[140px], and EVERY agent
                    name is wider than that — "AI Chemical Engineer" is ~144px
                    and "AI Plant Support Engineer" ~180px — so the label was
                    always ellipsised even though the trigger sits at the
                    bottom-left of a wide input card with obvious room to its
                    right. 260px clears the longest built-in name; the cap and
                    `truncate` stay because a custom persona name is
                    user-supplied and must not be able to run across the
                    composer. `title` keeps a clipped one readable. */}
                <span
                  className="min-w-0 truncate text-sm text-foreground"
                  title={agentDisplayName}
                >
                  {agentDisplayName}
                </span>
                <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side="top"
              sideOffset={8}
              // w-60 (240px) left ~184px for the label once the avatar, gap
              // and padding are out — under "AI Plant Support Engineer" at
              // ~180px only by a hair, so the longest entry sat on the edge of
              // clipping. w-72 gives it real headroom.
              className="w-72 p-1"
            >
              {/* Scope toggle — independent of the selected agent, so it
                  doesn't close the dropdown or change the active agent. Shared
                  with the empty-board entry card, which had no toggle at all
                  while this markup lived only here. */}
              {isFeatureAllowedInEmbed('catalogScope') && (
                <CatalogScopeToggle checked={entireDataCatalog} onChange={setEntireDataCatalog} />
              )}
              <div className="my-1 border-t border-border" />
              {visibleAgentEntries.map(([agentId, av]) => {
                const isActive = normalized === agentId
                const name = getDisplayName(agentId)
                return (
                  <button
                    key={agentId}
                    type="button"
                    onClick={() => {
                      setActiveAgent(agentId)
                      setAgentOpen(false)
                    }}
                    className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm transition-colors ${
                      isActive
                        ? 'bg-accent text-foreground font-medium'
                        : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                    }`}
                  >
                    <div
                      className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0"
                      style={{ backgroundColor: av.bgColor }}
                    >
                      <img src={av.image} alt={name} className="w-5 h-5 object-cover" draggable={false} />
                    </div>
                    <span className="truncate">{name}</span>
                  </button>
                )
              })}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* ── Scoped style overrides ────────────────────── */}
      <style>{`
/* Hide original ChatInterface <header> */
.board-chat-v2 header { display: none !important; }

/* Strip ChatInterface root card styling */
.board-chat-v2 > div:last-of-type > div.rounded-lg,
.board-chat-v2 .flex.flex-col.h-full.rounded-lg {
  border-radius: 0 !important;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

/* ── Empty-state: left-align, enlarge avatar to 56px ── */
.board-chat-v2 .text-center.flex-col {
  text-align: left !important;
  align-items: flex-start !important;
  padding: 16px 0 0 !important;
  gap: 12px !important;
}
.board-chat-v2 .text-center.flex-col figure { justify-content: flex-start !important; }
.board-chat-v2 .text-center.flex-col figure .w-16 { width: 56px !important; height: 56px !important; }
.board-chat-v2 .text-center.flex-col figure .w-16 img { width: 56px !important; height: 56px !important; }
.board-chat-v2 .text-center.flex-col h3 { font-size: 16px; font-weight: 500; line-height: 24px; color: #0a0a0a; }
.board-chat-v2 .text-center.flex-col p { font-size: 16px; font-weight: 400; line-height: 24px; color: #737373; }

/* ── Message-thread agent avatar → 32px ─────────────── */
/* FS-695: was 20px, too small to make out the persona in the picture. The
   img needs an explicit height too, or object-cover has no box to crop to. */
.board-chat-v2 .rounded-full > .w-10 { width: 32px !important; height: 32px !important; }
.board-chat-v2 .rounded-full > .w-10 img { width: 32px !important; height: 32px !important; }

/* ── User bubble: asymmetric corners, no border ──────── */
.board-chat-v2 .bg-gray-100.rounded-lg {
  border-radius: 8px 16px 8px 2px !important;
  padding: 12px 16px !important;
  border: none !important;
}

/* ── Messages area ─────────────────────────────────────── */
/* FS-539: keep top padding so the first user bubble isn't flush against the
   ContextHeader divider above it (the override previously zeroed it). */
.board-chat-v2 .overflow-y-auto { padding: 12px 16px; }

/* ═══════════════════════════════════════════════════════
   FOOTER: white bg (matches outer card; input stays white card w/ border)
   ═══════════════════════════════════════════════════════ */
.board-chat-v2 footer {
  border-top: none !important;
  padding: 0 !important;
  background: #ffffff !important;
  border-radius: 0 0 24px 24px !important;
}

/* Input row → vertical white card */
.board-chat-v2 footer > .flex {
  background: white !important;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
  margin: 0 16px 16px;
  padding: 0;
  flex-direction: column !important;
  gap: 0 !important;
  align-items: stretch !important;
  min-height: 120px;
  overflow: visible;
}

/* Textarea wrapper: top section */
.board-chat-v2 footer > .flex > .flex-col,
.board-chat-v2 footer > .flex > div:first-child {
  padding: 14px 16px 8px;
  flex: 1;
}

/* Textarea: white, borderless, no ring */
.board-chat-v2 footer textarea {
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  min-height: unset !important;
  resize: none;
  font-size: 16px;
  line-height: 24px;
  background: white !important;
  color: #0a0a0a;
}
/* IntuiTextarea wrapper div */
.board-chat-v2 footer .w-full:has(textarea) {
  background: white !important;
}
.board-chat-v2 footer textarea::placeholder { color: #737373; font-size: 16px; }
.board-chat-v2 footer textarea:focus,
.board-chat-v2 footer textarea:focus-visible {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
  ring: none !important;
}

/* Button bar: bottom-right of card, leave room for agent selector on the left */
.board-chat-v2 footer > .flex > .flex:last-child,
.board-chat-v2 footer > .flex > div:last-child:not(.flex-col):not(:first-child) {
  padding: 8px 16px 14px;
  width: 100%;
  display: flex;
  justify-content: flex-end;
}

/* Send / stop button: teal, 36×36, rounded-md */
.board-chat-v2 footer #chat-send-button,
.board-chat-v2 footer button[class*="bg-orange"] {
  background-color: #0e7490 !important;
  border-radius: 8px !important;
  width: 36px !important; height: 36px !important;
  min-width: 36px !important; min-height: 36px !important;
}
.board-chat-v2 footer #chat-send-button:hover,
.board-chat-v2 footer button[class*="bg-orange"]:hover {
  background-color: #155e75 !important;
}
.board-chat-v2 footer button:disabled { opacity: 0.5 !important; }

/* ═══════════════════════════════════════════════════════
   DARK MODE
   ═══════════════════════════════════════════════════════ */
.dark .board-chat-v2 footer { background: #262626 !important; }
.dark .board-chat-v2 footer > .flex { background: #1c1c1c !important; border-color: #404040; }
.dark .board-chat-v2 footer textarea { color: #fafafa; background: #1c1c1c !important; }
.dark .board-chat-v2 footer .w-full:has(textarea) { background: #1c1c1c !important; }
.dark .board-chat-v2 footer textarea::placeholder { color: #a3a3a3; }
.dark .board-chat-v2 .bg-gray-100.rounded-lg { background-color: #262626 !important; }
.dark .board-chat-v2 .text-center.flex-col h3 { color: #e5e5e5; }
.dark .board-chat-v2 .text-center.flex-col p { color: #a3a3a3; }
      `}</style>
    </div>
  )
}
